import {
  CheckCircle2,
  DollarSign,
  Eye,
  Lock,
  MapPin,
  Send,
  Truck,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MyFortAgentItem } from "@/api/types";
import { showInfo } from "@/lib/toast";
import { formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { C } from "@/components/modal/home";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import { CCC_FUSD_ADDRESS, USDT_ADDRESS } from "@/config/env";

/** 未激活：右侧信息区 */
export function MyAgentCardInactivePanel({
  agent,
  onAddress,
  onTransfer,
  onActivateClick,
  mustPayFusdBeforeActivate,
  activating,
  hasActivatedAgent,
}: {
  agent: MyFortAgentItem;
  onAddress: () => void;
  onTransfer: () => void;
  onActivateClick: () => void;
  mustPayFusdBeforeActivate: boolean;
  activating: boolean;
  /** 已有其他智能体处于已激活时，本卡仅可转让，不展示补缴/激活主按钮 */
  hasActivatedAgent: boolean;
}) {
  const { t } = useTranslation();
  const { openInactiveDetail, payFusdAgentId } = useMyAgentsUiStore();
  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const purchaseDateStr = dayjs(agent.createdAt).format("YYYY-MM-DD");

  return (
    <>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-black text-lg tracking-wide"
            style={{ color: C.muted }}
          >
            {agent.name}
          </span>
          <span
            className="text-[8px] px-1.5 py-[2px] rounded font-semibold"
            style={{
              background: `${tagColor}12`,
              color: tagColor,
              border: `1px solid ${tagColor}20`,
            }}
          >
            {tierLabel}
          </span>
          <span
            className="flex items-center gap-1 px-1.5 py-[2px] rounded text-[8px] font-bold ml-auto"
            style={{
              background: "rgba(212,165,116,0.06)",
              color: "#d4a574",
              border: "1px solid rgba(212,165,116,0.10)",
            }}
          >
            <Lock size={8} />
            {t("homePage.myAgentCard.statusPending")}
          </span>
        </div>
        <div className="mt-1 text-[10px]" style={{ color: C.dim }}>
          {t("homePage.myAgentCard.purchasedOnShort", {
            date: purchaseDateStr,
          })}
        </div>
      </div>

      {mustPayFusdBeforeActivate ? (
        <div
          className="rounded-lg p-2.5"
          style={{
            background: "rgba(239,68,68,0.04)",
            border: "1px solid rgba(239,68,68,0.10)",
          }}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <DollarSign size={12} style={{ color: C.red }} />
            <span
              className="text-[11px] font-semibold"
              style={{ color: C.red }}
            >
              {t("homePage.myAgentCard.fusdDebtTitle", {
                tokenName: getTokenName(CCC_FUSD_ADDRESS),
                tokenNameUsdt: getTokenName(USDT_ADDRESS),
              })}
            </span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-[11px]">
              <span style={{ color: C.muted }}>
                {t("homePage.myAgentCard.pendingFusdTotal")}
              </span>
              <span className="mono-num font-bold" style={{ color: C.red }}>
                {formatThousands(
                  formatEtherWithDecimals(agent.purchasePayFusdWei, 0),
                  0
                )}{" "}
                {getTokenName(CCC_FUSD_ADDRESS)}
              </span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentCard.priceBasisLabel")}
              </span>
              <span className="mono-num" style={{ color: C.muted }}>
                {formatThousands(
                  formatEtherWithDecimals(agent.priceBasisUsdt, 0),
                  0
                )}{" "}
                {getTokenName(USDT_ADDRESS)}
              </span>
            </div>
            <div className="flex justify-between text-[10px]">
              <span style={{ color: C.dim }}>
                {t("homePage.myAgentCard.paidUsdtLabel", {
                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                })}
              </span>
              <span className="mono-num" style={{ color: C.muted }}>
                {formatThousands(
                  formatEtherWithDecimals(agent.purchasePayUsdtTokenWei, 0),
                  0
                )}{" "}
                {getTokenName(USDT_ADDRESS)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg p-2 flex items-center gap-2"
          style={{
            background: "rgba(34,197,94,0.04)",
            border: `1px solid ${C.primary}10`,
          }}
        >
          <CheckCircle2 size={12} style={{ color: C.primary }} />
          <span className="text-[10px]" style={{ color: C.muted }}>
            {hasActivatedAgent
              ? t("homePage.myAgentCard.fullPaidReadyTransfer")
              : t("homePage.myAgentCard.fullPaidReadyActivate")}
          </span>
        </div>
      )}

      <div className="grid grid-cols-4 gap-1.5">
        {(
          [
            {
              icon: <MapPin size={10} />,
              label: t("homePage.myAgentCard.deliveryAddress"),
              onClick: onAddress,
            },
            {
              icon: <Truck size={10} />,
              label: t("homePage.myAgentCard.deliveryStatus"),
              onClick: () =>
                showInfo(t("homePage.myAgentCard.deliveryStatusToast")),
            },
            {
              icon: <Send size={10} />,
              label: t("homePage.myAgentCard.transfer"),
              onClick: onTransfer,
            },
            {
              icon: <Eye size={10} />,
              label: t("homePage.myAgentCard.viewDetail"),
              onClick: () => openInactiveDetail(agent.agentId),
            },
          ] as const
        ).map(item => (
          <button
            key={item.label}
            type="button"
            className="flex flex-col items-center gap-1 py-1.5 rounded-lg text-[9px] font-medium transition-all active:scale-95"
            style={{
              background: "rgba(255,255,255,0.02)",
              border: `1px solid ${C.border}`,
              color: C.muted,
            }}
            onClick={e => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
      {!hasActivatedAgent && (
        <button
          type="button"
          className="w-full py-2.5 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]"
          style={
            mustPayFusdBeforeActivate
              ? {
                  background: `${C.red}10`,
                  color: C.red,
                  border: `1px solid ${C.red}20`,
                }
              : {
                  background: `${C.primary}10`,
                  color: C.primary,
                  border: `1px solid ${C.primary}20`,
                }
          }
          onClick={onActivateClick}
          disabled={activating || payFusdAgentId === agent.agentId}
        >
          {activating ? (
            t("homePage.myAgentCard.activating")
          ) : mustPayFusdBeforeActivate ? (
            <>
              <DollarSign size={11} />
              {t("homePage.myAgentCard.payAndActivate")}
            </>
          ) : (
            <>
              <Zap size={11} />
              {t("homePage.myAgentCard.activate")}
            </>
          )}
        </button>
      )}
    </>
  );
}
